import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import { motion, AnimatePresence } from "framer-motion";
import { ShieldCheck, Wrench, Trophy, Gamepad2, Disc, Headphones, Joystick, ArrowRight, RefreshCw, Newspaper, Star, Tag, Ticket, Images, X, ChevronLeft, ChevronRight, MapPin, Camera, Store, ExternalLink } from "lucide-react";
import { api } from "../lib/api";
import ProductCard from "../components/ProductCard";
import {
  STORE_ADDRESS,
  STORE_HOURS,
  STORE_IMAGE,
  STORE_MAPS_LINK,
  STORE_PHONE,
  STORE_PHONE_HREF,
} from "../lib/storeInfo";

const HERO_BG = "/hero-bg.jpg";
const REPAIR_IMG = "https://static.prod-images.emergentagent.com/jobs/ab661ab1-b458-474a-b3a9-bee784d2d529/images/aafa7ce3a85f104c3d871c751cb4c0f20854854febe0db264715fd14904d3983.png";


const FIRST_SLIDE_VIDEO_ID = "QdBZY2fkU-0";

function HeroCarousel({ posts }) {
  const slides = posts.filter((p) => p.image);
  const [current, setCurrent] = useState(0);
  const [dir, setDir] = useState(1);

  // Auto-rotate only when NOT on the first (video) slide
  useEffect(() => {
    if (!slides.length || current === 0) return;
    const id = setInterval(() => {
      setDir(1);
      setCurrent((c) => {
        const next = (c + 1) % slides.length;
        // Skip back to 1 (not 0) to keep video slide sticky in auto-rotation
        return next === 0 ? 1 : next;
      });
    }, 4000);
    return () => clearInterval(id);
  }, [slides.length, current]);

  function goTo(i) {
    setDir(i > current ? 1 : -1);
    setCurrent(i);
  }

  if (!slides.length) return null;
  const slide = slides[current];
  const isFirstSlide = current === 0;

  return (
    <div className="relative w-full h-full flex flex-col gap-3">
      {/* Image/Video frame */}
      <div className="relative block w-full rounded-xl overflow-hidden shadow-2xl bg-navy-800" style={{ paddingBottom: "56.25%" }}>
        {isFirstSlide ? (
          <Link to="/community/9578260f-c6dc-4b5c-b57f-3426f22ec357" className="absolute inset-0 z-10" />
        ) : null}
        {isFirstSlide ? (
          <iframe
            key="video-slide"
            src={`https://www.youtube.com/embed/${FIRST_SLIDE_VIDEO_ID}?autoplay=1&mute=1&rel=0&start=3`}
            title={slide.title}
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            className="absolute inset-0 w-full h-full border-0"
          />
        ) : (
          <Link to={`/community/${slide.id}`} className="absolute inset-0">
            <motion.img
              key={current}
              src={slide.image}
              alt={slide.title}
              initial={{ opacity: 0, x: dir * 30 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.4, ease: "easeOut" }}
              className="w-full h-full object-cover"
            />
          </Link>
        )}
      </div>

      {/* Caption */}
      {slide.title && (
        <motion.div
          key={current + "-caption"}
          initial={{ opacity: 0, y: 6 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.35, delay: 0.15 }}
          className="flex items-center justify-center gap-2 px-2"
        >
          <span className="h-px w-6 bg-white/30 shrink-0" />
          <p className="text-center text-[11px] font-bold uppercase tracking-[0.18em] text-white truncate drop-shadow-[0_0_8px_rgba(255,255,255,0.5)]">
            {slide.title}
          </p>
          <span className="h-px w-6 bg-white/30 shrink-0" />
        </motion.div>
      )}

      {/* Thumbnail indicators */}
      <div className="flex gap-2 justify-center flex-wrap">
        {slides.map((s, i) => (
          <button
            key={i}
            onClick={() => goTo(i)}
            className={`w-28 h-16 rounded-lg overflow-hidden border-2 transition-all duration-300 shrink-0 ${
              i === current ? "border-gold opacity-100" : "border-transparent opacity-50 hover:opacity-80"
            }`}
          >
            {i === 0 ? (
              <img
                src={`https://img.youtube.com/vi/${FIRST_SLIDE_VIDEO_ID}/mqdefault.jpg`}
                alt="video"
                className="w-full h-full object-cover"
              />
            ) : (
              <img src={s.image} alt={s.title} className="w-full h-full object-cover" />
            )}
          </button>
        ))}
      </div>
    </div>
  );
}

const categories = [
  { id: "consoles", name: "Consoles", icon: Gamepad2, desc: "PlayStation & more" },
  { id: "games", name: "Games", icon: Disc, desc: "New, Used & Upcoming" },
  { id: "controllers", name: "Controllers", icon: Joystick, desc: "DualSense, Joy-Con, DualShock" },
  { id: "accessories", name: "Accessories", icon: Headphones, desc: "Headsets, keyboards" },
  { id: "digital", name: "Digital", icon: Ticket, desc: "Vouchers & Codes" },
];

const TAG_STYLES = {
  news: "bg-sky-500/20 text-sky-300 border-sky-500/30",
  release: "bg-violet-500/20 text-violet-300 border-violet-500/30",
  sale: "bg-emerald-500/20 text-emerald-300 border-emerald-500/30",
  discussion: "bg-gold/20 text-gold border-gold/30",
};
const TAG_LABELS = { news: "News", release: "Release", sale: "Sale / Deal", discussion: "Discussion" };

const GOOGLE_REVIEWS = [
  {
    name: "Abhilash Balabantaray", rating: 5, date: "22-04-2025",
    text: "Finally made my dream purchase today from Games Trade! I've been planning to get a PS5 for a long time—ever since my childhood days when I owned a PS2, which was my last console. The team at Games Trade was incredibly knowledgeable and offered me the best deal, making it a great value-for-money bundle. Their accessory recommendations were minimal yet useful, and they even made sure I got the color I wanted. The delivery was impressively quick—right to my doorstep within an hour—and they helped me with the entire setup too. Overall, it was an outstanding experience, one of the best I've had.",
    photos: ["/reviews/r11.png", "/reviews/r12.png"],
    link: "https://maps.app.goo.gl/Thzv4PMyGXwjtoqv8",
  },
  {
    name: "Arunesh Bahadur", rating: 5, date: "08-11-2023",
    text: "Purchased Spiderman 2 for PS5. The shop is well stocked with latest ps5 games and have ps5 consoles and accessories available. They also have home delivery and I was able to procure the game just a day after the worldwide launch with a good discount and complimentary goodies and DLC. Highly recommended for all gamers out there! 👍👍👍",
    photos: ["/reviews/r51.png", "/reviews/r52.png", "/reviews/r53.png"],
    link: "https://maps.app.goo.gl/1oryq5rr6TgPdp7s7",
  },
  {
    name: "Anuj", rating: 5, date: "03-05-2025",
    text: "What a great service. Faster than quick commerce apps, got a better price than online and then instant delivery. Highly recommended offline store. Ordered Ghost of Yotei...got it in 20 mins",
    photos: ["/reviews/r2.png"],
    link: "https://maps.app.goo.gl/CeDVhjbrVTEF2NNH8",
  },
  {
    name: "Mr Manoj", rating: 5, date: "28-04-2025",
    text: "They are really amazing. They came after close the shop for me and today is my birthday and they specially come after close the shop. It's a very good recommended. Try giving good discount also, so must try.",
    photos: ["/reviews/r4.png"],
    link: "https://maps.app.goo.gl/1J5BhDpCzKPzM51P7",
  },
  {
    name: "Guy at the Gig", rating: 5, date: "10-04-2025",
    text: "First experience with Games Trade Noida, \"absolutely fantastic\"... I wanted to get reliable hall effect sticks for my PS5 dual sense controller and their name popped up. They managed to replace them within a day and it works flawlessly. Super quick solution and very professional. Icing on the cake was that they were kind enough to have the controller delivered to my home. Thank you Priyanka, Varun and Sanju for making my life easier! 💯",
    photos: [],
    link: "https://maps.app.goo.gl/5PtxKe71J5UcLKBk7",
  },
  {
    name: "Dhruv Kaushik", rating: 5, date: "15-04-2025",
    text: "Walking into the shop was a breeze—super easy to find. The staff was incredibly polite, patiently answering my multiple calls and offering a great discount. They explained everything clearly and had impressive knowledge about the PlayStation, making my purchase smooth and enjoyable.",
    photos: ["/reviews/r6.png"],
    link: "https://maps.app.goo.gl/Vo4jVSuLoXb7cAJh8",
  },
];

function ReviewLightbox({ review, onClose }) {
  const [idx, setIdx] = useState(0);
  const photos = review.photos ?? [];
  const prev = () => setIdx((i) => (i - 1 + photos.length) % photos.length);
  const next = () => setIdx((i) => (i + 1) % photos.length);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowLeft" && photos.length > 1) prev();
      if (e.key === "ArrowRight" && photos.length > 1) next();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [photos.length, onClose]);

  return (
    <AnimatePresence>
      <motion.div
        className="fixed inset-0 z-50 flex items-center justify-center bg-black/85 backdrop-blur-sm p-4"
        initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="relative bg-navy-800 rounded-2xl overflow-hidden w-full max-w-lg flex flex-col"
          initial={{ scale: 0.93, opacity: 0 }} animate={{ scale: 1, opacity: 1 }} exit={{ scale: 0.93, opacity: 0 }}
          transition={{ duration: 0.2 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close */}
          <button onClick={onClose} className="absolute top-3 right-3 z-10 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition">
            <X className="w-4 h-4" />
          </button>

          {/* Review header */}
          <div className="p-5 pb-3 flex items-center justify-between pr-12">
            <div className="text-sm font-bold">{review.name}</div>
            <a
              href={review.link} target="_blank" rel="noopener noreferrer"
              className="shrink-0 opacity-60 hover:opacity-100 transition"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
              </svg>
            </a>
          </div>

          <div className="px-5 pb-1"><StarRating n={review.rating} /></div>
          <p className="px-5 py-3 text-sm text-slate-300 leading-relaxed">{review.text}</p>

          {/* Date + photos bottom row */}
          <div className="px-5 pb-4 flex items-center justify-between">
            {photos.length > 0 ? (
              <div className="flex items-center gap-1.5 text-[11px] font-bold text-gold/70">
                <Images className="w-3.5 h-3.5" />
                {photos.length} photo{photos.length > 1 ? "s" : ""}
              </div>
            ) : <span />}
            <div className="text-[10px] text-slate-500">Reviewed {review.date}</div>
          </div>

          {/* Photos */}
          {photos.length > 0 && (
            <div className="px-5 pb-5 flex flex-col gap-3">
              <div className="relative rounded-xl overflow-hidden bg-navy-900 aspect-[4/3]">
                <img key={idx} src={photos[idx]} alt="" className="w-full h-full object-cover" />
                {photos.length > 1 && (
                  <>
                    <div className="absolute top-2 left-2 bg-black/60 text-white text-xs font-bold px-2 py-0.5 rounded-full">
                      {idx + 1} / {photos.length}
                    </div>
                    <button onClick={prev} className="absolute left-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition">
                      <ChevronLeft className="w-4 h-4" />
                    </button>
                    <button onClick={next} className="absolute right-2 top-1/2 -translate-y-1/2 bg-black/60 hover:bg-black/80 text-white rounded-full p-1.5 transition">
                      <ChevronRight className="w-4 h-4" />
                    </button>
                  </>
                )}
              </div>
              {photos.length > 1 && (
                <div className="flex gap-2">
                  {photos.map((src, i) => (
                    <button key={i} onClick={() => setIdx(i)} className={`w-14 h-14 rounded-lg overflow-hidden border-2 transition shrink-0 ${i === idx ? "border-gold" : "border-transparent opacity-50 hover:opacity-80"}`}>
                      <img src={src} alt="" className="w-full h-full object-cover" />
                    </button>
                  ))}
                </div>
              )}
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
}

function StarRating({ n }) {
  return (
    <div className="flex gap-0.5">
      {[1, 2, 3, 4, 5].map((i) => (
        <Star key={i} className={`w-3.5 h-3.5 ${i <= n ? "fill-gold text-gold" : "text-slate-600"}`} />
      ))}
    </div>
  );
}

function FlagshipStoreSection() {
  const [showPhoto, setShowPhoto] = useState(true);

  return (
    <section className="site-container py-16">
      <div className="grid lg:grid-cols-[1.05fr_0.95fr] gap-6 items-stretch">
        <div className="relative overflow-hidden rounded-2xl border border-gold/30 bg-navy-800 min-h-[320px]">
          {showPhoto ? (
            <img
              src={STORE_IMAGE}
              alt="GamesTrade flagship store in Noida"
              className="absolute inset-0 w-full h-full object-cover"
              onError={() => setShowPhoto(false)}
            />
          ) : (
            <div className="absolute inset-0 bg-navy-800">
              <div className="absolute inset-0 bg-grid opacity-35" />
              <div className="relative h-full flex flex-col items-center justify-center text-center px-8">
                <Camera className="w-12 h-12 text-gold mb-4" />
                <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Store photo coming soon</div>
                <p className="text-sm text-slate-300 max-w-sm">Add your store image at <span className="text-white font-semibold">public/store-noida.jpg</span> and this panel will pick it up automatically.</p>
              </div>
            </div>
          )}
          <div className="absolute inset-0 bg-gradient-to-t from-navy-900 via-navy-900/45 to-transparent" />
          <div className="absolute left-5 bottom-5 right-5">
            <div className="inline-flex items-center gap-2 rounded-full bg-black/55 border border-gold/30 px-3 py-1 text-[10px] font-bold uppercase tracking-[0.22em] text-gold backdrop-blur-sm">
              <Store className="w-3.5 h-3.5" /> Noida Flagship Store
            </div>
          </div>
        </div>

        <div className="relative overflow-hidden rounded-2xl border border-gold/30 bg-gradient-to-br from-navy-700 to-navy-900 p-8 lg:p-10 flex flex-col justify-center">
          <div className="absolute inset-0 bg-grid opacity-20" />
          <div className="relative">
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-3">Visit the flagship</div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter uppercase leading-tight">
              Our Noida store is built for <span className="text-gold">hands-on gaming.</span>
            </h2>
            <p className="mt-4 text-sm text-slate-300 leading-relaxed">
              Drop by Spectrum Mall for consoles, games, accessories, pre-owned deals, trade-ins and repair support from the GamesTrade team.
            </p>
            <div className="mt-6 space-y-3 text-sm text-slate-300">
              <a href={STORE_MAPS_LINK} target="_blank" rel="noopener noreferrer" className="flex items-start gap-3 hover:text-gold transition">
                <MapPin className="w-5 h-5 text-gold shrink-0 mt-0.5" />
                <span>{STORE_ADDRESS}</span>
              </a>
              <div className="flex items-center gap-3">
                <ShieldCheck className="w-5 h-5 text-gold shrink-0" />
                <span>Sony India Authorised - {STORE_HOURS}</span>
              </div>
            </div>
            <div className="mt-7 flex flex-wrap gap-3">
              <a href={STORE_MAPS_LINK} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-2 bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-6 py-3 rounded-full hover:bg-gold-light btn-glow transition-all">
                Get Directions <ExternalLink className="w-4 h-4" />
              </a>
              <a href={STORE_PHONE_HREF} className="inline-flex items-center gap-2 border border-gold/40 text-gold font-extrabold uppercase tracking-wider text-sm px-6 py-3 rounded-full hover:bg-gold/10 transition-all">
                Call {STORE_PHONE}
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function HomePage({ preloaded }) {
  const [featured, setFeatured] = useState(preloaded?.featured ?? []);
  const [used, setUsed] = useState(preloaded?.used ?? []);
  const [posts, setPosts] = useState(preloaded?.posts ?? []);
  const [lightbox, setLightbox] = useState(null); // { photos, index }

  useEffect(() => {
    if (preloaded) return; // data already fetched by BootLoader
    (async () => {
      const [f, u, p] = await Promise.all([
        api.get("/products", { params: { featured: true, limit: 5 } }),
        api.get("/products", { params: { condition: "used", limit: 4 } }),
        api.get("/posts/home"),
      ]);
      setFeatured(f.data);
      setUsed(u.data);
      setPosts(p.data);
    })();
  }, [preloaded]);

  return (
    <div>
      <Helmet>
        <title>{"GamesTrade — You Play We Trade"}</title>
        <meta name="description" content="India's trusted gaming destination in Noida, Sector 75. Buy new, sell used, trade-in PS5, Nintendo Switch, Xbox. Sony Authorised dealer. 25+ years." />
        <link rel="canonical" href="https://gamestrade.in/" />
        <meta property="og:title" content="GamesTrade — You Play We Trade | Noida" />
        <meta property="og:description" content="India's trusted gaming destination. Buy, sell and trade PS5, Nintendo Switch, Xbox. Sony Authorised. 25+ years. Spectrum Mall, Sector 75, Noida." />
        <meta property="og:url" content="https://gamestrade.in/" />
        <meta property="og:type" content="website" />
      </Helmet>
      {/* HERO */}
      <section className="relative h-[90vh] min-h-[580px] flex items-center overflow-hidden">
        <div className="absolute inset-0">
          <img src={HERO_BG} alt="" className="w-full h-full object-cover" />
          <div className="absolute inset-0 hero-overlay" />
          <div className="absolute inset-0 bg-grid opacity-40" />
        </div>
        <div className="relative site-container w-full flex flex-col lg:flex-row items-center gap-10">
          <motion.div className="flex-1 max-w-[480px] min-w-[280px]" initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}>
            <div className="inline-flex items-center gap-2 bg-gold/10 border border-gold/40 backdrop-blur-sm px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.25em] text-gold mb-5">
              <ShieldCheck className="w-3 h-3" /> Sony Authorised • 25+ Years Legacy
            </div>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tighter uppercase leading-[0.95]">
              You Play<br /><span className="text-gold text-glow-yellow">We Trade</span><br /><span className="inline-block mt-2">Games<span className="text-gold text-glow-yellow">Trade</span></span>
            </h1>
            <p className="mt-5 text-sm sm:text-base text-slate-300 leading-relaxed">
              India's trusted destination for PlayStation, Nintendo & everything gaming. Buy new, sell used, trade-in for credit — all under one neon-lit roof.
            </p>
            <div className="mt-7 flex flex-wrap gap-3">
              <Link to="/shop" data-testid="hero-shop-cta" className="bg-gold text-navy-900 font-extrabold uppercase tracking-wider text-sm px-7 py-3.5 rounded-full hover:bg-gold-light btn-glow transition-all">
                Shop Now <ArrowRight className="inline ml-1 w-4 h-4" />
              </Link>
            </div>
          </motion.div>

          {/* Hero carousel */}
          {/* Hero carousel */}
          <motion.div
            className="flex-1 min-w-0 w-full lg:w-auto flex flex-col justify-center mt-8 lg:mt-0"
            initial={{ opacity: 0, x: 20 }}
            animate={{ opacity: 1, x: 0 }}
            transition={{ duration: 0.7, delay: 0.2 }}
          >
            <HeroCarousel posts={posts} />
          </motion.div>
        </div>
      </section>

      {/* TRUST STRIP */}
      <section className="bg-navy-700/50 border-y border-gold/10 py-6">
        <div className="site-container grid grid-cols-2 md:grid-cols-5 gap-6 text-center">
          {[
            { i: ShieldCheck, t: "Sony Authorised", s: "Official warranty" },
            { i: Trophy, t: "25+ Years", s: "Trusted legacy" },
            { i: RefreshCw, t: "Trade-In Credits", s: "Best buyback value" },
            { i: Wrench, t: "In-House Repairs", s: "Expert team" },
            { i: Star, t: "Rated 4.9", s: "Google Reviews" },
          ].map((b, i) => (
            <div key={i} className="flex items-center justify-center gap-3">
              <b.i className="w-6 h-6 text-gold shrink-0" />
              <div className="text-left">
                <div className="text-sm font-extrabold uppercase tracking-wider">{b.t}</div>
                <div className="text-[10px] text-slate-400 uppercase tracking-wide">{b.s}</div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* CATEGORIES BENTO */}
      <section className="site-container py-20">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Browse</div>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tighter uppercase">Pick your <span className="text-gold">poison</span></h2>
          </div>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {categories.map((c) => (
            <Link
              key={c.id}
              to={`/shop?category=${c.id}`}
              data-testid={`category-card-${c.id}`}
              className="group relative bg-navy-700 rounded-2xl p-6 neon-border hover:-translate-y-1 transition-all duration-300 overflow-hidden"
            >
              <c.icon className="w-10 h-10 text-gold mb-4 group-hover:scale-110 transition-transform" />
              <div className="text-lg font-black uppercase tracking-tight whitespace-nowrap">{c.name}</div>
              <div className="text-xs text-slate-400 uppercase tracking-wider mt-1 pr-6">{c.desc}</div>
              <ArrowRight className="absolute bottom-6 right-6 w-5 h-5 text-gold/40 group-hover:text-gold group-hover:translate-x-1 transition" />
            </Link>
          ))}
        </div>
      </section>

      {/* FEATURED */}
      <section className="site-container py-12">
        <div className="flex items-end justify-between mb-8">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Hot Right Now</div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter uppercase">Featured drops</h2>
          </div>
          <Link to="/shop" className="text-sm font-bold uppercase tracking-wider text-gold hover:underline">View all →</Link>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-4">
          {featured.map((p) => <ProductCard key={p.id} p={p} />)}
        </div>
      </section>

      <FlagshipStoreSection />

      {/* USED DEALS */}
      {used.length > 0 && (
        <section className="site-container py-12">
          <div className="flex items-end justify-between mb-8">
            <div>
              <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Certified Pre-owned</div>
              <h2 className="text-3xl sm:text-4xl font-black tracking-tighter uppercase">Used deals</h2>
            </div>
            <Link to="/shop?condition=used" className="text-sm font-bold uppercase tracking-wider text-gold hover:underline">All used →</Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
            {used.map((p) => <ProductCard key={p.id} p={p} />)}
          </div>
        </section>
      )}

      {/* GOOGLE REVIEWS */}
      <section className="site-container py-16">
        <div className="flex items-end justify-between mb-10">
          <div>
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2 flex items-center gap-2">
              <Star className="w-4 h-4 fill-gold" /> Google Reviews
            </div>
            <h2 className="text-3xl sm:text-4xl font-black tracking-tighter uppercase">What our customers <span className="text-gold">say</span></h2>
          </div>
          <div className="text-right hidden sm:block">
            <div className="flex items-center gap-1 justify-end mb-1">
              {[1, 2, 3, 4, 5].map((i) => <Star key={i} className="w-5 h-5 fill-gold text-gold" />)}
            </div>
            <div className="text-2xl font-black text-gold">4.9</div>
            <div className="text-xs text-slate-400 uppercase tracking-wider">Google Rating</div>
          </div>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {GOOGLE_REVIEWS.map((r, i) => (
            <button
              key={i}
              onClick={() => setLightbox(r)}
              className="bg-navy-700 rounded-2xl p-5 neon-border flex flex-col gap-3 text-left hover:bg-navy-600 hover:-translate-y-0.5 transition-all duration-200 cursor-pointer"
            >
              {/* Header */}
              <div className="flex items-center justify-between w-full">
                <div className="text-sm font-bold">{r.name}</div>
                <a
                  href={r.link} target="_blank" rel="noopener noreferrer"
                  onClick={(e) => e.stopPropagation()}
                  className="shrink-0 opacity-50 hover:opacity-100 transition"
                >
                  <svg className="w-5 h-5" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z" fill="#4285F4" />
                    <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                    <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                    <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                  </svg>
                </a>
              </div>
              <StarRating n={r.rating} />
              <p className="text-sm text-slate-300 leading-relaxed line-clamp-3">{r.text}</p>
              <div className="flex items-center justify-between mt-auto pt-1">
                {r.photos?.length > 0 ? (
                  <div className="flex items-center gap-1.5 text-[11px] font-bold text-gold/70">
                    <Images className="w-3.5 h-3.5" />
                    {r.photos.length} photo{r.photos.length > 1 ? "s" : ""}
                  </div>
                ) : <span />}
                <div className="text-[10px] text-slate-500">Reviewed on {r.date}</div>
              </div>
            </button>
          ))}
        </div>
      </section>

      {/* SERVICES */}
      <section className="site-container py-20 grid lg:grid-cols-3 gap-6">

        {/* REPAIR */}
        <div className="relative rounded-2xl overflow-hidden neon-border group min-h-[300px] flex flex-col justify-end">
          <img src={REPAIR_IMG} alt="" className="absolute inset-0 w-full h-full object-cover opacity-50 group-hover:opacity-70 transition-opacity duration-300" />
          <div className="absolute inset-0 bg-gradient-to-tr from-navy-900 via-navy-900/70 to-transparent" />
          <div className="relative p-8 max-w-md">
            <Wrench className="w-10 h-10 text-gold mb-4" />
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Service</div>
            <h3 className="text-3xl font-black uppercase mb-2">Console & controller repairs</h3>
            <p className="text-sm text-slate-300 leading-relaxed">Free diagnostics. Genuine parts. 90-day service warranty by certified technicians.</p>
            <Link to="/repairs" className="mt-3 inline-flex items-center text-gold font-bold text-sm uppercase tracking-wider hover:underline">
              Learn more <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* TRADE-IN */}
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-gold/10 via-navy-700 to-navy-900 neon-border group min-h-[300px] flex flex-col justify-end">
          <div className="absolute inset-0 bg-grid opacity-20" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/25" />
          <div className="relative p-8 max-w-md">
            <RefreshCw className="w-10 h-10 text-gold mb-4" />
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Trade In</div>
            <h3 className="text-3xl font-black uppercase mb-2">Old gear into store credit</h3>
            <p className="text-sm text-slate-300 leading-relaxed">Selling your PS4, old Switch, or gaming gear? Get a quick quote, fair buyback value, and instant credit.</p>
            <Link to="/trade-in" className="mt-3 inline-flex items-center text-gold font-bold text-sm uppercase tracking-wider hover:underline">
              Start trade-in <ArrowRight className="ml-1 w-4 h-4" />
            </Link>
          </div>
        </div>

        {/* LEGACY */}
        <div className="relative rounded-2xl overflow-hidden bg-gradient-to-br from-navy-700 to-navy-900 neon-border group min-h-[300px] flex flex-col justify-end">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_left,_rgba(245,197,36,0.06),_transparent_45%)] group-hover:opacity-80 transition-opacity duration-300" />
          <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black/20" />
          <div className="relative p-8 max-w-md">
            <Trophy className="w-10 h-10 text-gold mb-4" />
            <div className="text-xs font-bold uppercase tracking-[0.25em] text-gold mb-2">Legacy</div>
            <div className="flex items-center gap-4 mb-2">
              <div className="text-6xl font-black text-gold leading-none opacity-90">25+</div>
              <div className="leading-tight relative top-[2px]">
                <h3 className="text-2xl font-black uppercase">Years of</h3>
                <h3 className="text-2xl font-black uppercase text-gold">Gaming</h3>
              </div>
            </div>
            <p className="text-sm text-slate-300 leading-relaxed">Two decades of curating all the tech from the OG PlayStation to PS5 Pro — we've been there for it all.</p>
            <Link to="/about" className="mt-3 inline-flex items-center text-gold font-bold text-sm uppercase tracking-wider hover:underline">
              Explore legacy →
            </Link>
          </div>
        </div>
      </section>

      {lightbox && (
        <ReviewLightbox
          review={lightbox}
          onClose={() => setLightbox(null)}
        />
      )}
    </div>
  );
}
